import React, { useState, useRef, useEffect } from "react";
import {
  Select,
  MenuItem,
  Box,
  FormControl,
  Typography,
  TextField,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";

import { ReactComponent as CheckedIcon } from "../../../assets/Checkbox.svg";
import { ReactComponent as UnCheckedIcon } from "../../../assets/unCheckbox.svg";
import SearchIcon from "@mui/icons-material/Search";

import CommonColorUtils from "../../../utils/common_colors";
import StringUtils from "../../../utils/string_utils";

const styles = {
  formControl: {
    width: "120px",
    mr: 1,
    mt: 1.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      alignItems: "flex-start",
      paddingTop: "2px",
      paddingBottom: "2px",
      minHeight: "48px",
    },
  },
  select: {
    "& .MuiSelect-select": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "4px 12px",
      gap: "2px",
    },
  },
  labelText: {
    fontSize: 12,
    color: CommonColorUtils.primaryGrey600,
  },
  valueText: {
    fontSize: 14,
    fontWeight: 600,
    color: "#000",
    lineHeight: 1.3,
  },
  menuPaper: {
    maxHeight: 320,
    overflowY: "auto",

    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none" },

    paddingTop: 0,
    borderRadius: "8px",
    border: `1px solid ${CommonColorUtils.kborderColorPortal}`,
    boxShadow: "none",
  },
  searchContainer: {
    px: 1,
    py: 0.5,
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 2,
  },
  searchField: {
    "& .MuiInputBase-input": { fontSize: 14, py: 0.5 },
    "& .MuiOutlinedInput-root": { height: 34 },
  },
  menuItem: {
    padding: "6px",
    margin: "6px 8px",
    borderRadius: "4px",
    minHeight: 34,
    display: "flex",
    alignItems: "center",
    "&:hover": { backgroundColor: CommonColorUtils.khoverColor },
  },
  iconBox: {
    display: "flex",
    alignItems: "center",
    mr: 1,
    svg: { width: 16, height: 16 },
  },
  optionText: {
    fontSize: 14,
    fontWeight: 500,
  },
  noResults: {
    color: CommonColorUtils.noResultsTextColor,
    fontSize: 14,
  },
};

const VendorDropdown = ({
  vendorList = [],
  selectedVendors = [],
  onChange = () => {},
  disabled
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  const filteredList = vendorList.filter((vendor) =>
    vendor.vendorName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isSelected = (name) =>
    selectedVendors.some((v) => v.vendorName === name);

  const handleSelectAll = () => {
    if (selectedVendors.length === vendorList.length) {
      onChange([]);
    } else {
      onChange(vendorList);
    }
  };

  const handleSelect = (vendorName) => {
    const vendor = vendorList.find((v) => v.vendorName === vendorName);
    if (!vendor) return;

    if (isSelected(vendorName)) {
      onChange(selectedVendors.filter((v) => v.vendorName !== vendorName));
    } else {
      onChange([...selectedVendors, vendor]);
    }
  };

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 80);
    }
  }, [open]);

  const displayValue =
    selectedVendors.length === 0
      ? StringUtils.defaultSelectLabel
      : selectedVendors.map((v) => v.vendorName).join(", ");

  return (
    <FormControl sx={styles.formControl}>
      <Select
        multiple
        displayEmpty
        disabled={disabled} 
        open={open}
        onOpen={() => !disabled && setOpen(true)} 
        onClose={() => {
          setOpen(false);
          setSearchQuery("");
        }}
        value={selectedVendors.map((v) => v.vendorName)}
        input={<OutlinedInput />}
        renderValue={() => (
          <Box>
            <Typography sx={styles.labelText}>{StringUtils.vendor}</Typography>
            <Typography sx={styles.valueText}>{displayValue}</Typography>
          </Box>
        )}
        MenuProps={{
          PaperProps: { style: styles.menuPaper },
          autoFocus: false,
        }}
        sx={styles.select}
        data-testid="vendor-dropdown"
      >
        <Box sx={styles.searchContainer} onKeyDown={(e) => e.stopPropagation()}>
          <TextField
            data-testid="vendor-search-input"
            inputRef={inputRef}
            disabled={disabled} 
            fullWidth
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={StringUtils.searchLabel}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={styles.searchField}
          />
        </Box>

        <MenuItem
          onClick={!disabled ? handleSelectAll : undefined}
          data-testid="vendor-select-all"
          sx={{
            ...styles.menuItem,
            backgroundColor:
              selectedVendors.length === vendorList.length &&
              vendorList.length > 0
                ? CommonColorUtils.primaryGrey300
                : "white",
          }}
        >
          <Box sx={styles.iconBox}>
            {selectedVendors.length === vendorList.length &&
            vendorList.length > 0 ? (
              <CheckedIcon />
            ) : (
              <UnCheckedIcon />
            )}
          </Box>
          <Typography sx={styles.optionText}>({StringUtils.selectAll})</Typography>
        </MenuItem>

        {filteredList.length > 0 ? (
          filteredList.map((vendor) => {
            const selected = isSelected(vendor.vendorName);
            return (
              <MenuItem
                key={vendor.vendorId}
                data-testid={`vendor-option-${vendor?.vendorId}`} 
                onClick={!disabled ? () => handleSelect(vendor.vendorName) : undefined}
                sx={{
                  ...styles.menuItem,
                  backgroundColor: selected
                    ? CommonColorUtils.primaryGrey300
                    : "white",
                }}
              >
                <Box sx={styles.iconBox}>
                  {selected ? <CheckedIcon /> : <UnCheckedIcon />}
                </Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: selected ? 600 : 500,
                  }}
                >
                  {vendor.vendorName}
                </Typography>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>
            <Typography sx={styles.noResults}>
              {StringUtils.noResultFound}
            </Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default VendorDropdown;
